import { inject, Pipe, PipeTransform } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Title, UserRef } from '../models/access.models';
import { fmtDate } from '../services/access-grant-view.service';
import { RightField, rightText } from '../util/access-right-text';
import { avatarColor, initialsOf, phoneOf, pluralForm, spaced, titleIn } from '../util/access-format';

/** `{{ node.title | accessTitle }}`: the title in the language of the page. */
@Pipe({ name: 'accessTitle' })
export class AccessTitlePipe implements PipeTransform {
  private readonly tr = inject(TranslocoService);

  transform(title: Title | null | undefined): string {
    return titleIn(title, this.tr.getActiveLang());
  }
}

/** `{{ 5 | accessPlural: 'access.plural.employees' }}` → «сотрудников»: the translation holds the forms «сотрудник|сотрудника|сотрудников». */
@Pipe({ name: 'accessPlural' })
export class AccessPluralPipe implements PipeTransform {
  private readonly tr = inject(TranslocoService);

  transform(n: number, key: string): string {
    return pluralForm(this.tr.translate(key), n, this.tr.getActiveLang());
  }
}

@Pipe({ name: 'accessInitials' })
export class AccessInitialsPipe implements PipeTransform {
  transform(name: string | null | undefined): string {
    return initialsOf(name ?? '');
  }
}

/** The colour of the round avatar of a person: the same person has the same colour everywhere. */
@Pipe({ name: 'accessAvatar' })
export class AccessAvatarPipe implements PipeTransform {
  transform(id: number): string {
    return avatarColor(id);
  }
}

@Pipe({ name: 'accessPhone' })
export class AccessPhonePipe implements PipeTransform {
  transform(user: UserRef): string {
    return phoneOf(user);
  }
}

/** `{{ key | accessRight }}` → the title of the right; `| accessRight: 'desc'` → its description. A key without a text shows its title or the key, never `access.rights.x.y`. */
@Pipe({ name: 'accessRight' })
export class AccessRightPipe implements PipeTransform {
  private readonly tr = inject(TranslocoService);

  transform(key: string, field: RightField = 'title'): string {
    return rightText(k => this.tr.translate(k), key, field);
  }
}

/** 1104 → «1 104». */
@Pipe({ name: 'accessNum' })
export class AccessNumberPipe implements PipeTransform {
  transform(n: number): string {
    return spaced(n);
  }
}

/** `{{ iso | accessDate }}` → «31.12.2026»: the calendar date as the server wrote it. */
@Pipe({ name: 'accessDate' })
export class AccessDatePipe implements PipeTransform {
  transform(iso: string | null | undefined): string {
    return fmtDate(iso);
  }
}

export const ACCESS_PIPES = [AccessTitlePipe, AccessPluralPipe, AccessInitialsPipe, AccessAvatarPipe, AccessPhonePipe, AccessNumberPipe, AccessDatePipe, AccessRightPipe] as const;
